"use client";

import { LayoutGridIcon, ListIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { ABOUT_ART, HalftoneArt } from "../../about/_components/halftone-art";
import {
  AboutSection,
  SectionTitle,
  bodyCopy,
  mutedPanel,
} from "../../about/_components/section";
import { TEAM, TEAM_SIZE, type TeamMember } from "./team-data";

const MEMBERS: TeamMember[] = TEAM.flatMap((group) => group.members);
const UNLISTED = TEAM_SIZE - MEMBERS.length;

/**
 * The team strip on /about and the whole of /team. One component, so the two
 * pages cannot disagree about who works here.
 */
export function TeamSection({ as = "h2" }: { as?: "h1" | "h2" }) {
  const [view, setView] = useState<"cards" | "list">("cards");
  const [open, setOpen] = useState<string | null>(null);
  const selected = MEMBERS.find((m) => m.name === open) ?? null;

  return (
    <AboutSection id="team">
      {/* Phones crop the skyline wider than the screen so the towers stay
          legible instead of shrinking to a strip. */}
      <div className="-mx-4 mb-8 overflow-hidden sm:mx-0 sm:mb-16 sm:overflow-visible">
        <HalftoneArt
          art={ABOUT_ART.skyline}
          label="The San Francisco skyline rising out of the fog, set in halftone"
          className="bg-marketing-blue -mx-[22%] w-[144%] sm:mx-0 sm:w-full"
        />
      </div>
      <div className="flex flex-col">
        <div className="mb-8 flex flex-col items-start gap-3 sm:mb-10 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <SectionTitle as={as}>
            We&apos;re {TEAM_SIZE} people, based in San Francisco
          </SectionTitle>
          <button
            type="button"
            onClick={() => {
              setView(view === "cards" ? "list" : "cards");
            }}
            className="text-marketing-text-muted hover:text-marketing-text inline-flex shrink-0 items-center gap-1.5 text-sm transition-colors"
          >
            {view === "cards" ? (
              <ListIcon className="size-4" aria-hidden="true" />
            ) : (
              <LayoutGridIcon className="size-4" aria-hidden="true" />
            )}
            {view === "cards" ? "List view" : "Card view"}
          </button>
        </div>

        {view === "cards" ? (
          <>
            <ul className="scrollbar-hide -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 md:-mx-8 md:scroll-px-8 md:px-8">
              {MEMBERS.map((m) => (
                <li key={m.name} className="shrink-0 snap-start">
                  <button
                    type="button"
                    aria-expanded={open === m.name}
                    aria-controls="team-bio"
                    aria-label={`About ${m.name}`}
                    onClick={() => {
                      setOpen(open === m.name ? null : m.name);
                    }}
                    className="group flex w-[200px] flex-col gap-3 text-left sm:w-[223px]"
                  >
                    <span
                      className={cn(
                        "relative block aspect-[223/293] w-full overflow-hidden rounded-xl ring-offset-2 transition-shadow",
                        open === m.name && "ring-marketing-blue ring-2",
                      )}
                      style={{ background: m.cardColor }}
                    >
                      {m.image ? (
                        <Image
                          src={m.image}
                          alt=""
                          fill
                          sizes="223px"
                          className="object-cover transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none"
                        />
                      ) : null}
                    </span>
                    <span className="flex flex-col">
                      <span className="text-marketing-text text-[15px] font-medium">
                        {m.name}
                      </span>
                      <span className="text-marketing-text-muted text-sm">
                        {m.role}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
              {UNLISTED > 0 ? (
                <li className="shrink-0 snap-start">
                  <div className="flex w-[200px] flex-col gap-3 sm:w-[223px]">
                    <div
                      className={cn(
                        mutedPanel,
                        "flex aspect-[223/293] w-full flex-col items-center justify-center gap-2 text-center",
                      )}
                    >
                      <span className="text-marketing-blue text-[44px] leading-none font-semibold tracking-[-0.05em]">
                        +{UNLISTED}
                      </span>
                      <span className="text-marketing-text-muted px-6 text-sm">
                        more in San Francisco
                      </span>
                    </div>
                    <Link
                      href="/about#roles"
                      className="text-marketing-text text-[15px] font-medium underline decoration-1 underline-offset-4"
                    >
                      And you?
                    </Link>
                  </div>
                </li>
              ) : null}
            </ul>
            <div id="team-bio" aria-live="polite" className="mt-8 empty:mt-0">
              {selected ? (
                <div
                  className={cn(mutedPanel, "flex max-w-3xl gap-4 p-6 sm:p-7")}
                >
                  <Headshot member={selected} className="size-14" />
                  <div className="flex flex-col gap-2">
                    <p className="text-marketing-text font-medium">
                      {selected.name}{" "}
                      <span className="text-marketing-text-muted font-normal">
                        · {selected.role}
                      </span>
                    </p>
                    <p className={bodyCopy}>{selected.bio}</p>
                    {selected.linkedin ? (
                      <Link
                        href={selected.linkedin}
                        className="text-marketing-blue text-sm"
                      >
                        LinkedIn &rarr;
                      </Link>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <ul className="border-marketing-border grid grid-cols-1 border-t md:grid-cols-2 md:gap-x-16">
            {MEMBERS.map((m) => (
              <li
                key={m.name}
                className="border-marketing-border flex gap-4 border-b py-5"
              >
                <Headshot member={m} className="size-12" />
                <div className="flex flex-col gap-1">
                  <span className="text-marketing-text font-medium">
                    {m.name}{" "}
                    <span className="text-marketing-text-muted font-normal">
                      · {m.role}
                    </span>
                  </span>
                  <span className="text-marketing-text-muted text-sm leading-[1.6]">
                    {m.bio}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AboutSection>
  );
}

function Headshot({
  member,
  className,
}: {
  member: TeamMember;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "relative block shrink-0 overflow-hidden rounded-full",
        className,
      )}
      style={{ background: member.cardColor }}
    >
      {member.image ? (
        <Image
          src={member.image}
          alt=""
          fill
          sizes="56px"
          className="object-cover object-[center_20%]"
        />
      ) : null}
    </span>
  );
}
